import {Alert, Button, Card, Col, Collapse, Divider, Row, Space, Tooltip} from "antd";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilValue} from "recoil";
import {useInitializeUserVerificationHistory} from "../../../../function/accountVerification";
import {verificationRefetchState} from "../../../../state";
import {useIsMobile} from "../../../../../../_common/function";
import {extractLocalizedValue, getStatusColor} from "../../../../common";
import {VerificationStep, isPendingLikeStatus} from "../../../../common/constant";
import {resolveActionableTier} from "../../../../lib/resolveStep";
import {
    isStandaloneOutstanding,
    isStandaloneTier,
    resolveTierListing,
} from "../../../../lib/standalone";
import {
    apiCancelVerificationHistory,
    apiDuplicateUserVerificationHistory
} from "../../../../../../api/restful/verification";
import {
    CheckCircleOutlined,
    FileOutlined,
    LockOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";

function SelectTier(props) {
    const {
        userVerificationHistories,
        goTo,
        setSelectedTier,
        userVerificationTiers,
        accessRightsListCategory,
        blockedByFailedTier,
        failedTierHistory,
        blockedTierHistory,
    } = props

    const {t} = useTranslation();
    const isMobile = useIsMobile()
    const {initializeUserVerificationHistory} = useInitializeUserVerificationHistory();
    const {
        refetchUserVerificationProfile,
        refetchUserVerificationHistories,
    } = useRecoilValue(verificationRefetchState);
    const [isCancelling, setIsCancelling] = useState(false)

    const handleCancelBlockedTier = async () => {
        if (!blockedTierHistory?._id) return;
        try {
            setIsCancelling(true);
            await apiCancelVerificationHistory(blockedTierHistory._id);
            await Promise.all([
                refetchUserVerificationProfile(),
                refetchUserVerificationHistories(),
            ]);
        } catch (e) {
            console.error("handleCancelBlockedTier error:", e);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleSelectTier = async (tier, startNewRecord = false) => {
        setSelectedTier(tier);
        const existingHistory = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(history => (history.tierSetting?._id || history.tierSetting) === tier._id)
        const recordExist = ["canceled", "verification_failed"].includes(existingHistory?.status)

        if (!existingHistory?._id || (recordExist && startNewRecord)) {
            try {
                const result = await initializeUserVerificationHistory(tier._id);

                if (result?.status === "Success") {
                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    if (startNewRecord) return

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    return () => clearTimeout(timeout);
                }

            } catch (error) {
                console.error("error initializing verification history:", error);
            }
        } else if (recordExist) {
            try {
                const result = await apiDuplicateUserVerificationHistory({_id: existingHistory?._id})

                if (result?.status === "Success") {
                    await Promise.all([
                        refetchUserVerificationProfile(),
                        refetchUserVerificationHistories()
                    ]);

                    const timeout = setTimeout(() => {
                        goTo(VerificationStep.Document);
                    }, 150);

                    return () => clearTimeout(timeout);
                }
            } catch (e) {
                console.error("error duplicating verification history:", e);
            }
        } else {
            goTo(VerificationStep.Document);
        }
    }

    const getTierStatus = (tierId) => {
        const history = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .find((h) => (h.tierSetting?._id || h.tierSetting) === tierId);
        return history?.status ?? null;
    }

    // A case can ask for SEVERAL standalone verifications at once, so every
    // outstanding one is listed rather than just the first. While any is
    // outstanding the ladder is suppressed; once they are all dealt with it
    // comes back.
    const {standaloneTiers, showStandalone, ladderTiers} =
        resolveTierListing(userVerificationTiers, userVerificationHistories)

    // Lowest tier the client can act on now (shared walk). A failed lower tier
    // is surfaced even when a higher tier is already submitted/pending, so its
    // resubmit button stays enabled instead of reading "Unavailable".
    //
    // Ladder tiers only: every standalone sits at tierLevel 0, and the walk
    // keeps one record per level, so including them lets two standalone tiers
    // collide on a single history.
    const currentTierAvailable = resolveActionableTier(ladderTiers, userVerificationHistories).tier
    const currentTierAvailableId = currentTierAvailable?._id

    const mapCategoryAccess = (category, access) => {
        const result = {}

        for (const [catKey, catValues] of Object.entries(category || {})) {
            const arr = []

            for (const [accKey, accVal] of access) {
                if (catValues[accKey]) {
                    arr.push([accKey, accVal])
                }
            }

            if (arr.length > 0) {
                result[catKey] = arr
            }
        }

        return result
    }

    if (blockedByFailedTier) {
        const blockedTierSettingId = blockedTierHistory?.tierSetting?._id || blockedTierHistory?.tierSetting;
        const failedTierSettingId = failedTierHistory?.tierSetting?._id || failedTierHistory?.tierSetting;
        const blockedTier = userVerificationTiers?.find(t => t._id === blockedTierSettingId) || (blockedTierHistory?.tierSetting?._id ? blockedTierHistory.tierSetting : null);
        const failedTier = userVerificationTiers?.find(t => t._id === failedTierSettingId) || (failedTierHistory?.tierSetting?._id ? failedTierHistory.tierSetting : null);
        const blockedTierName = extractLocalizedValue(blockedTier?.displayName) || blockedTier?.name;
        const failedTierName = extractLocalizedValue(failedTier?.displayName) || failedTier?.name;

        return (
            <Row gutter={[16, 16]}>
                {blockedTier && (
                    <Col span={24}>
                        <Card className="account-verification-card-selection h-full">
                            <div className="corner-tag-group">
                                {blockedTier?.tierLevel && (
                                    <span className="corner-status-tag status-geekblue">
                                        {t("Tier")} {blockedTier.tierLevel}
                                    </span>
                                )}
                                <span
                                    className={`corner-status-tag status-${getStatusColor(blockedTierHistory.status)}`}>
                                    {t(`accountVerification.status.${blockedTierHistory.status}`) || blockedTierHistory.status.replace(/_/g, " ")}
                                </span>
                            </div>
                            <Space direction="vertical" size={8} style={{width: "100%"}}>
                                <div className="account-verification-card-selection-title">{blockedTierName}</div>
                                <Alert
                                    type="warning"
                                    showIcon
                                    message={t("accountVerification.blockedTier.alert", { failedLevel: failedTierHistory.tierLevel, failedTierName, blockedLevel: blockedTierHistory.tierLevel })}
                                />
                                <Row className="mt-2">
                                    <Col xs={24} md={12}>
                                        <Button
                                            danger
                                            block={isMobile}
                                            loading={isCancelling}
                                            onClick={handleCancelBlockedTier}
                                        >
                                            {t("accountVerification.blockedTier.cancelButton", { blockedLevel: blockedTierHistory.tierLevel })}
                                        </Button>
                                    </Col>
                                </Row>
                            </Space>
                        </Card>
                    </Col>
                )}
                {failedTier && (
                    <Col span={24}>
                        <Tooltip title={t("accountVerification.blockedTier.tooltip", { blockedLevel: blockedTierHistory.tierLevel })}>
                            <Card className="account-verification-card-selection h-full" style={{opacity: 0.6}}>
                                <div className="corner-tag-group">
                                    {failedTier?.tierLevel && (
                                        <span className="corner-status-tag status-geekblue">
                                            {t("Tier")} {failedTier.tierLevel}
                                        </span>
                                    )}
                                    <span
                                        className={`corner-status-tag status-${getStatusColor(failedTierHistory.status)}`}>
                                        {t(`accountVerification.status.${failedTierHistory.status}`) || failedTierHistory.status.replace(/_/g, " ")}
                                    </span>
                                </div>
                                <Space direction="vertical" size={8} style={{width: "100%"}}>
                                    <div className="account-verification-card-selection-title">{failedTierName}</div>
                                    <Space size={6}>
                                        <LockOutlined/>
                                        <span className="secondary-text">
                                            {t("accountVerification.blockedTier.lockHint", { blockedLevel: blockedTierHistory.tierLevel })}
                                        </span>
                                    </Space>
                                </Space>
                            </Card>
                        </Tooltip>
                    </Col>
                )}
            </Row>
        );
    }

    return (
        <Row gutter={[16, 16]}>
            {/* Layouts 2 and 3 normally show just the one tier the client can act
                on. When nothing is actionable — every ladder tier verified while
                standalone sit in review — that used to render an EMPTY list.
                It was unreachable while `hasPending` sent those clients to the
                under-review screen; now that landing prefers the tier list, the
                fallback has to show the full set with their status tags. */}
                        {(showStandalone ? standaloneTiers : (currentTierAvailable ? [currentTierAvailable] : userVerificationTiers))?.map((tier) => {
                const displayName = extractLocalizedValue(tier.displayName) || tier.name;
                const description = extractLocalizedValue(tier.description);
                const status = getTierStatus(tier._id);
                const tierVerificationHistory = [...(userVerificationHistories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).find(history => (history.tierSetting?._id || history.tierSetting) === tier._id);
                const displayStatus = status && tierVerificationHistory?.documents !== null;
                const documents = tier?.documentSettings?.map(doc => extractLocalizedValue(doc?.documentSettingDoc?.displayName) || doc?.documentSettingDoc?.name)

                const accessRightsList = Object.entries(tier?.accessRights || {}) || []
                const access = accessRightsList.filter(item => item[1] === null)
                const accessWithAmounts = accessRightsList.filter(item => item[1] !== null)
                // A standalone is gated on its own state, never on the ladder's
                // cursor: several can be open at once, and the cursor can only
                // ever point at one of them.
                //
                // The ladder half dropped a `!currentTierAvailableId && tierLevel
                // <= 1` clause: with every ladder tier verified the cursor is
                // null, so that enabled "Proceed" on an ALREADY VERIFIED tier 1.
                // It was unreachable while such clients landed on the
                // under-review screen instead of this list.
                const nextButtonAvailable = isStandaloneTier(tier)
                    ? isStandaloneOutstanding(tier, userVerificationHistories)
                    : tier?._id === currentTierAvailableId
                const accessWithCategory = mapCategoryAccess(accessRightsListCategory, access);

                return (
                    <Col className={"mh-100"} span={24} key={tier._id}>
                        <Tooltip placement="top">
                            <Card className="account-verification-card-selection h-full">
                                <div className="corner-tag-group">
                                    {tier?.tierLevel ? (
                                        <span className="corner-status-tag status-geekblue">
                                            {t("Tier")} {tier?.tierLevel}
                                        </span>
                                    ) : null}
                                    {displayStatus ? (
                                        <span className={`corner-status-tag status-${getStatusColor(status)}`}>
                                            {t(`accountVerification.status.${status}`) || status.replace(/_/g, " ")}
                                        </span>
                                    ) : null}
                                </div>
                                <Space direction="vertical" size={8} style={{width: "100%", height: "100%"}}>
                                    <div className="account-verification-card-selection-title">{displayName}</div>

                                    <p><small>{description}</small></p>

                                    <Row gutter={[8, 16]}>
                                        {documents?.length > 0 && (
                                            <Col span={isMobile ? 24 : 12}>
                                                <p className="mb-2 text-xs tier-section-label">Documents Required</p>
                                                <Space direction="vertical" wrap size={4}>
                                                    {documents?.map(d => (
                                                        <div className="documents-tag" key={d}>
                                                            <FileOutlined className="text-md"/> {d}
                                                        </div>
                                                    ))}
                                                </Space>
                                                {isMobile && <Divider className="my-2"/>}

                                                {nextButtonAvailable && ["canceled", "verification_failed"].includes(tierVerificationHistory?.status) && (
                                                    <Row className="mt-4" gutter={[8, 8]}
                                                         onClick={(e) => e.stopPropagation()}>
                                                        <Col xs={24} md={14}>
                                                            <Button type="primary" block
                                                                    onClick={() => handleSelectTier(tier)}>
                                                                {t("Continue with last record")}
                                                            </Button>
                                                        </Col>
                                                        <Col xs={24} md={9}>
                                                            <Button block onClick={() => handleSelectTier(tier, true)}>
                                                                {t("Start New Record")}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                                {nextButtonAvailable && !["canceled", "verification_failed"].includes(tierVerificationHistory?.status) && (
                                                    <Row className="mt-4" gutter={[8, 8]}>
                                                        <Col span={22}>
                                                            <Button type="primary" block
                                                                    onClick={() => handleSelectTier(tier)}>
                                                                {t("Proceed")}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </Col>
                                        )}

                                        <Col span={isMobile ? 24 : (documents?.length > 0 ? 12 : 24)}>
                                            {accessWithAmounts?.filter(([_, {
                                                dailyMax,
                                                allTimeMax
                                            }]) => dailyMax > 0 || allTimeMax > 0)?.length > 0 && (
                                                <>
                                                    <p className="tier-limits-inline-title">Account Limits After
                                                        Verification</p>
                                                    <Space direction="vertical" size={0} style={{width: "100%"}}>
                                                        {accessWithAmounts
                                                            ?.filter(([_, {
                                                                dailyMax,
                                                                allTimeMax
                                                            }]) => dailyMax > 0 || allTimeMax > 0)
                                                            ?.map(([module, {dailyMax, allTimeMax}], idx) => (
                                                                <Row key={module} justify="space-between" align="middle"
                                                                     className={`verification-limit-row${idx > 0 ? " verification-limit-row-divider" : ""}`}>
                                                                    <Space size={8}>
                                                                        <LockOutlined
                                                                            className="verification-limit-icon"/>
                                                                        <span className="tier-limit-label">
                                                                            {t(`accountVerification.tierSettingAccessRight.${module}`, module.replace(/_/g, " "))}
                                                                        </span>
                                                                    </Space>
                                                                    <span className="verification-limit-value">
                                                                        {dailyMax > 0
                                                                            ? `${Number(dailyMax).toLocaleString()} USD Daily`
                                                                            : allTimeMax > 0 && allTimeMax !== -1
                                                                                ? `${Number(allTimeMax).toLocaleString()} USD`
                                                                                : "Unlimited"}
                                                                    </span>
                                                                </Row>
                                                            ))}
                                                    </Space>
                                                </>
                                            )}
                                            {Object.keys(accessWithCategory).length > 0 && (
                                                <>
                                                    {accessWithAmounts?.filter(([_, {
                                                        dailyMax,
                                                        allTimeMax
                                                    }]) => dailyMax > 0 || allTimeMax > 0)?.length > 0 && (
                                                        <Divider className="my-2"/>
                                                    )}
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <Collapse
                                                            expandIconPosition="end"
                                                            ghost
                                                            size="small"
                                                            items={[{
                                                                key: "features",
                                                                label: (
                                                                    <Space align="center">
                                                                        <SafetyCertificateOutlined className="text-md"/>
                                                                        <span
                                                                            className="text-xs">Features Unlocked</span>
                                                                    </Space>
                                                                ),
                                                                children: (
                                                                    <div className="tier-modules-compact">
                                                                        {Object.values(accessWithCategory).flat().map(([module]) => (
                                                                            <span className="tier-module-chip"
                                                                                  key={module}>
                                                                                <CheckCircleOutlined/>
                                                                                {t(`accountVerification.tierSettingAccessRight.${module}`)}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ),
                                                            }]}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                </Space>
                            </Card>
                        </Tooltip>
                    </Col>
                );
            })}
        </Row>
    )
}

export default SelectTier
